import { useState, useEffect } from "react";
import Cookies from "universal-cookie";
import { MdMail } from "react-icons/md";
import ReactRouterPrompt from "react-router-prompt";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaMinus, FaPlus } from "react-icons/fa";

import "../../stylesheets/Settings.scss";

import { Title } from "../../components/Title.jsx";
import LoadingIcon from "../../components/LoadingIcon";
import { RenderWarehouses } from "./RenderWarehouses.jsx";
import { RenderTruckers } from "./RenderTruckers.jsx";
import { AccountDropdown } from "./AccountDropdown.jsx";
import { loadingAnimation } from "../../components/scripts/functions.js";

export const Settings = () => {
  const URL = process.env.REACT_APP_API_URL + "/user";
  const cookies = new Cookies();
  const token = cookies.get("token_user");

  // Active Tab state
  const [activeTab, setActiveTab] = useState("personal"); // 'personal' | 'notifications' | 'truckers' | 'warehouses'

  function normalizeEmails(cc_emails) {
    if (Array.isArray(cc_emails)) return cc_emails;
    if (cc_emails && typeof cc_emails === "object") return Object.values(cc_emails);
    if (typeof cc_emails === "string") return [cc_emails];
    return [];
  }

  const [userSettings, setUserSettings] = useState();
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Notification Email States - array of objects: { email: string, accounts: string[] }
  const [docsRequiredList, setDocsRequiredList] = useState([]);
  const [entrySummaryList, setEntrySummaryList] = useState([]);
  const [_7501List, set7501List] = useState([]);
  const [billingList, setBillingList] = useState([]);
  const [deliveryList, setDeliveryList] = useState([]);

  const [truckerError, setTruckerError] = useState();
  const [warehouseError, setWarehouseError] = useState();

  // Baseline snapshot for detecting true unsaved changes
  const [initialSnapshot, setInitialSnapshot] = useState("");

  const createSnapshot = (user, docs, entry, f7501, bill, del, passVal) => {
    if (!user) return "";
    return JSON.stringify({
      email: user.email || "",
      password: passVal || "",
      docsRequired: docs || [],
      entrySummary: entry || [],
      form7501: f7501 || [],
      billing: bill || [],
      delivery: del || [],
      truckers: (user.truckers || []).map((t) => ({
        name: t.name || "",
        phone: t.phone || "",
        emails: t.trucker_emails || [],
        accounts: t.accounts || [],
      })),
      warehouses: (user.warehouses || []).map((w) => ({
        name: w.name || "",
        phone: w.phone || "",
        address: w.address || "",
        city: w.city || "",
        state: w.state || "",
        zip: w.zip || "",
        emails: w.emails || [],
        accounts: w.accounts || [],
      })),
    });
  };

  useEffect(() => {
    getUserSettings();
  }, []);

  const getUserSettings = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${URL}/settings`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();

      if (resp.status === 200) {
        const user = data.user;
        setUserSettings(user);

        const accounts = (user.accounts || [])
          .map((a) => (typeof a === "object" ? a.account_no : a))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));

        const initList = (relData) => {
          const accountEmails = relData?.[0]?.account_emails;
          if (Array.isArray(accountEmails) && accountEmails.length > 0) {
            return accountEmails.map((item) => ({
              email: item.email || "",
              accounts: Array.isArray(item.accounts) ? item.accounts : [...accounts],
            }));
          }

          const main = relData?.[0]?.email;
          const cc = normalizeEmails(relData?.[0]?.cc_emails);
          const combined = [];
          if (main) combined.push(main);
          cc.forEach((e) => {
            if (e && !combined.includes(e)) combined.push(e);
          });
          if (combined.length === 0) combined.push("");

          return combined.map((emailStr) => ({
            email: emailStr,
            accounts: [...accounts],
          }));
        };

        const initDeliveryList = (deliveryMails) => {
          const accountEmails = deliveryMails?.[0]?.account_emails;
          if (Array.isArray(accountEmails) && accountEmails.length > 0) {
            return accountEmails.map((item) => ({
              email: item.email || "",
              accounts: Array.isArray(item.accounts) ? item.accounts : [...accounts],
            }));
          }

          if (!deliveryMails || deliveryMails.length === 0) {
            return [{ email: "", accounts: [...accounts] }];
          }
          const items = [];
          deliveryMails.forEach((m) => {
            const accNo = m.account_no;
            const ccList = normalizeEmails(m.cc_emails);
            ccList.forEach((emailStr) => {
              if (emailStr) {
                items.push({
                  email: emailStr,
                  accounts: accNo ? [accNo] : [...accounts],
                });
              }
            });
          });
          if (items.length === 0) {
            items.push({ email: "", accounts: [...accounts] });
          }
          return items;
        };

        const docs = initList(user.document_emails);
        const entry = initList(user.entry_summary_emails);
        const f7501 = initList(user._7501_emails);
        const bill = initList(user.invoice_emails);
        const del = initDeliveryList(user.delivery_order_mails);

        setDocsRequiredList(docs);
        setEntrySummaryList(entry);
        set7501List(f7501);
        setBillingList(bill);
        setDeliveryList(del);

        // Store initial snapshot
        const snapshot = createSnapshot(user, docs, entry, f7501, bill, del, "");
        setInitialSnapshot(snapshot);

        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  // Determine if user has made unsaved changes
  const currentSnapshot = createSnapshot(
    userSettings,
    docsRequiredList,
    entrySummaryList,
    _7501List,
    billingList,
    deliveryList,
    newPassword
  );

  const hasUnsavedChanges =
    Boolean(initialSnapshot) && currentSnapshot !== initialSnapshot;

  const receiveTruckers = (truckers) => {
    setUserSettings((prev) => ({ ...prev, truckers: truckers }));
    submitCompleteInfo(null, { truckers });
  };

  const receiveWarehouses = (warehouses) => {
    setUserSettings((prev) => ({ ...prev, warehouses: warehouses }));
    submitCompleteInfo(null, { warehouses });
  };

  const setNewAccountEmail = (email) => {
    setUserSettings({ ...userSettings, email: email });
  };

  // Helper to add/remove/update emails in notification lists
  const handleEmailListChange = (setter, list, index, field, value) => {
    const updated = [...list];
    updated[index] = { ...updated[index], [field]: value };
    setter(updated);
  };

  const handleAddEmailRow = (setter, list) => {
    const accounts = (userSettings?.accounts || []).map((a) => a.account_no);
    setter([...list, { email: "", accounts: [...accounts] }]);
  };

  const handleRemoveEmailRow = (setter, list, index) => {
    setter(list.filter((_, i) => i !== index));
  };

  const submitCompleteInfo = async (e, overrides = {}) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsSaving(true);
    setTruckerError("");
    setWarehouseError("");

    // Prepare notification arrays for API
    const extractEmails = (list) => list.map((item) => item.email).filter(Boolean);

    const docEmails = extractEmails(docsRequiredList);
    const summaryEmails = extractEmails(entrySummaryList);
    const form7501Emails = extractEmails(_7501List);
    const invoiceEmails = extractEmails(billingList);
    const doEmails = extractEmails(deliveryList);

    const emailsArray = [
      {
        table_number: 0,
        email: docEmails[0] || null,
        cc_emails: docEmails.slice(1),
        account_emails: docsRequiredList,
      },
      {
        table_number: 2,
        email: form7501Emails[0] || null,
        cc_emails: form7501Emails.slice(1),
        account_emails: _7501List,
      },
      {
        table_number: 1,
        email: invoiceEmails[0] || null,
        cc_emails: invoiceEmails.slice(1),
        account_emails: billingList,
      },
      {
        table_number: 5,
        email: summaryEmails[0] || null,
        cc_emails: summaryEmails.slice(1),
        account_emails: entrySummaryList,
      },
      {
        table_number: 3,
        cc_emails: doEmails.length > 0 ? doEmails : null,
        account_emails: deliveryList,
      },
    ];

    const settingsObj = {};
    settingsObj["user_emails"] = emailsArray;

    const sourceTruckers = overrides.truckers ?? userSettings?.truckers ?? [];
    const truckers = sourceTruckers.map((trucker) => {
      return {
        id: trucker.isNew ? null : trucker.id,
        name: trucker.name,
        phone: trucker.phone,
        email: trucker?.trucker_emails?.[0]?.email,
        cc_emails: trucker?.trucker_emails?.[0]?.cc_emails,
        accounts: trucker.accounts,
      };
    });
    settingsObj["truckers"] = truckers;

    const sourceWarehouses = overrides.warehouses ?? userSettings?.warehouses ?? [];
    const warehouses = sourceWarehouses.map((warehouse) => {
      return {
        id: warehouse.isNew ? null : warehouse.id,
        name: warehouse.name,
        phone: warehouse.phone,
        address: warehouse.address,
        city: warehouse.city,
        state: warehouse.state,
        zip: warehouse.zip,
        accounts: warehouse.accounts,
        emails: (warehouse.emails || []).map((email) => {
          return { name: email.name, email: email.email };
        }),
      };
    });
    settingsObj["warehouses"] = warehouses;

    settingsObj["email"] = userSettings.email;

    if (newPassword.trim()) {
      settingsObj["password"] = newPassword;
    }

    try {
      loadingAnimation(true);

      const resp = await fetch(`${URL}/settings/save`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-type": "application/json",
        },
        body: JSON.stringify(settingsObj),
      });

      const res = await resp.json();

      setIsSaving(false);
      loadingAnimation(false);
      toast.success("Settings saved successfully!");

      setNewPassword("");
      await getUserSettings();
    } catch (e) {
      loadingAnimation(false);
      setIsSaving(false);
      toast.error("Something went wrong. Please, try again in a few minutes!");
    }
  };

  const accountList = (userSettings?.accounts || [])
    .map((a) => (typeof a === "object" ? a.account_no : a))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  const showAccountDropdown = accountList.length > 0;

  const renderEmailCategorySection = (title, subtitle, list, setter) => {
    return (
      <div className="input-field notification-category-field">
        <div className="left">
          <p className="field-title">{title}</p>
          <p className="field-small-text">{subtitle}</p>
        </div>
        <div className="right">
          <div className="notification-email-rows-container">
            {list.map((item, idx) => (
              <div key={idx} className="notification-email-row">
                <div className="email-input-wrapper">
                  <MdMail className="mail-icon" />
                  <input
                    type="email"
                    value={item.email}
                    onChange={(e) =>
                      handleEmailListChange(setter, list, idx, "email", e.target.value)
                    }
                    placeholder="Email Address"
                  />
                </div>

                {showAccountDropdown && (
                  <AccountDropdown
                    accounts={accountList}
                    selectedAccounts={item.accounts || []}
                    onChange={(newAccs) =>
                      handleEmailListChange(setter, list, idx, "accounts", newAccs)
                    }
                  />
                )}

                <button
                  type="button"
                  className="remove-email-row-btn"
                  onClick={() => handleRemoveEmailRow(setter, list, idx)}
                  title="Remove email"
                >
                  <FaMinus />
                </button>
              </div>
            ))}

            <button
              type="button"
              className="add-email-btn-outline"
              onClick={() => handleAddEmailRow(setter, list)}
            >
              <FaPlus className="plus-icon" /> Add Email
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <ReactRouterPrompt when={hasUnsavedChanges}>
        {({ isActive, onConfirm, onCancel }) => {
          if (isActive && hasUnsavedChanges) {
            if (
              window.confirm(
                "You have unsaved changes. Are you sure you want to leave?"
              )
            ) {
              onConfirm();
            } else {
              onCancel();
            }
          }
        }}
      </ReactRouterPrompt>

      {loading || typeof userSettings === "undefined" ? (
        <>
          <Title title="Settings" />
          <div id="settings" className="page-content">
            Loading...
          </div>
        </>
      ) : (
        <>
          <Title
            title="Settings"
            links={[
              { link: "/", title: "Home" },
              { link: "/settings", title: "Settings" },
            ]}
          />
          <div id="settings" className="page-content">
            {/* Top Sub-Navigation Tabs Bar */}
            <div className="settings-tabs-nav">
              <button
                type="button"
                className={`tab-btn ${activeTab === "personal" ? "active" : ""}`}
                onClick={() => setActiveTab("personal")}
              >
                Personal Information
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "notifications" ? "active" : ""}`}
                onClick={() => setActiveTab("notifications")}
              >
                Notifications
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "truckers" ? "active" : ""}`}
                onClick={() => setActiveTab("truckers")}
              >
                Truckers
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "warehouses" ? "active" : ""}`}
                onClick={() => setActiveTab("warehouses")}
              >
                Warehouses
              </button>
            </div>

            <form className="update-profile" onSubmit={submitCompleteInfo}>
              {/* Tab 1: Personal Information */}
              {activeTab === "personal" && (
                <div className="personal-information tab-content">
                  <div className="input-sections">
                    <div className="input-field account-name">
                      <div className="left">
                        <p className="field-title">Account Name</p>
                        <p className="field-small-text">
                          To edit this notify{" "}
                          <a
                            href="mailto:broker@atlanticfb.com"
                            className="notify-link"
                          >
                            broker@atlanticfb.com
                          </a>
                        </p>
                      </div>
                      <div className="right">
                        <div className="blocked-input">
                          <input
                            type="text"
                            readOnly
                            disabled
                            value={
                              userSettings.accounts?.length > 0
                                ? userSettings.accounts
                                    .map((account) => account.account_no)
                                    .join(", ")
                                : userSettings.name || "AGF"
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div className="divisor"></div>

                    <div className="input-field account-adress">
                      <div className="left">
                        <p className="field-title">Account Email Address</p>
                        <p className="field-small-text">
                          To edit this notify{" "}
                          <a
                            href="mailto:broker@atlanticfb.com"
                            className="notify-link"
                          >
                            broker@atlanticfb.com
                          </a>
                        </p>
                      </div>
                      <div className="right">
                        <div className="allowed-input has-icon">
                          <input
                            type="email"
                            name="email"
                            id="email-input"
                            placeholder="Email Address"
                            value={userSettings.email || ""}
                            onChange={(e) => {
                              e.preventDefault();
                              setNewAccountEmail(e.target.value);
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="divisor"></div>

                    <div className="input-field account-password">
                      <div className="left">
                        <p className="field-title">Password</p>
                        <p className="field-small-text">
                          Enter a new password to reset your password
                        </p>
                      </div>
                      <div className="right">
                        <div className="allowed-input has-icon">
                          <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            id="password-input"
                            placeholder="Password"
                            value={newPassword}
                            onChange={(e) => {
                              e.preventDefault();
                              setNewPassword(e.target.value);
                            }}
                          />
                          <div
                            className="input-icon"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2: Notifications */}
              {activeTab === "notifications" && (
                <div className="notifications tab-content">
                  <div className="input-sections">
                    {renderEmailCategorySection(
                      "Documents Required",
                      "Where should reminders to upload documents be sent?",
                      docsRequiredList,
                      setDocsRequiredList
                    )}

                    <div className="divisor"></div>

                    {renderEmailCategorySection(
                      "Entry Summary Docs",
                      "Email address for summary package document",
                      entrySummaryList,
                      setEntrySummaryList
                    )}

                    <div className="divisor"></div>

                    {renderEmailCategorySection(
                      "7501 Form",
                      "Where should we send the 7501 form once the shipment is released?",
                      _7501List,
                      set7501List
                    )}

                    <div className="divisor"></div>

                    {renderEmailCategorySection(
                      "Billing Invoice",
                      "Where should billing invoices be sent?",
                      billingList,
                      setBillingList
                    )}

                    <div className="divisor"></div>

                    {renderEmailCategorySection(
                      "Delivery Order",
                      "Email addresses for Delivery Orders",
                      deliveryList,
                      setDeliveryList
                    )}
                  </div>
                </div>
              )}

              {/* Tab 3: Truckers */}
              {activeTab === "truckers" && (
                <div className="truckers tab-content">
                  <RenderTruckers
                    truckers={userSettings.truckers}
                    sendTruckers={receiveTruckers}
                    accounts={userSettings.accounts}
                  />
                  {truckerError && <p className="error-msg">{truckerError}</p>}
                </div>
              )}

              {/* Tab 4: Warehouses */}
              {activeTab === "warehouses" && (
                <div className="warehouses tab-content">
                  <RenderWarehouses
                    warehouses={userSettings?.warehouses}
                    userId={userSettings.id}
                    sendWarehouses={receiveWarehouses}
                    accounts={userSettings.accounts}
                  />
                  {warehouseError && <p className="error-msg">{warehouseError}</p>}
                </div>
              )}

              {hasUnsavedChanges && (
                <div className="alert">You have several unsaved changes.</div>
              )}

              <button type="submit" className="settings-save-btn">
                {isSaving ? <LoadingIcon /> : "Save"}
              </button>
            </form>
          </div>
        </>
      )}
    </>
  );
};
export default Settings;
